import React, { HTMLAttributes } from "react";
import * as PropTypes from "prop-types";
import ReactDOM from "react-dom";
import _ from "lodash";
import Helper from "helper";
import "./LayoutChooser.styl";

/**
 * @description LayoutChooser UI Component Properties
 *
 * @interface ILayoutChooserProps
 * @extends {HTMLAttributes<HTMLDivElement>}
 */
interface ILayoutChooserProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * @description 레이아웃 선택을 위한 표의 행 갯수
   */
  rows: number;
  /**
   * @description 레이아웃 선택을 위한 표의 열 갯수
   */
  columns: number;
  /**
   * @description 표의 한 셀에 대한 클릭 이벤트 발생시 호출되는 콜백 함수
   *              row - 셀의 행 인덱스
   *              col - 셀의 열 인덱스
   */
  onCellClick?: ((row: number, col: number) => void) | null;
}

/**
 * @description LayoutChooser UI Class
 *
 * @class LayoutChooser
 * @extends {React.PureComponent<ILayoutChooserProps, any>}
 */
class LayoutChooser extends React.PureComponent<ILayoutChooserProps, any> {
  static propTypes: PropTypes.InferProps<ILayoutChooserProps>;

  /**
   * @description default property values.
   * 기본 모양은 4x4 표
   * @static
   * @memberof LayoutChooser
   */
  public static defaultProps = {
    rows: 4,
    columns: 4,
    onCellClick: () => {}
  };

  /**
   * @description 표 전체 와 표의 각 셀에 이벤트 핸들러 등록
   *
   * @memberof LayoutChooser
   */
  componentDidMount = () => {
    const domNode: any = ReactDOM.findDOMNode(this);

    const table = domNode.querySelectorAll(".layoutChooser table")[0];
    table.addEventListener("mouseleave", this.handlerTableMouseLeave);

    let cellElements = domNode.querySelectorAll(".layoutChooser table td");
    cellElements.forEach((element: any) => {
      element.addEventListener(
        "touchstart",
        this.handlerCellMouseHover,
        Helper.Browser.APITest.isSupportPassiveListener()
          ? { passive: true }
          : false
      );
      element.addEventListener("mouseenter", this.handlerCellMouseHover);
      element.addEventListener("mouseleave", this.handlerCellMouseLeave);
      element.addEventListener("click", this.handlerCellMouseClick);
    });
  };

  /**
   * @description 표와 표의 각 셀에 등록한 이벤트 핸들러 제거
   *
   * @memberof LayoutChooser
   */
  componentWillUnmount = () => {
    const domNode: any = ReactDOM.findDOMNode(this);

    const table = domNode.querySelectorAll(".layoutChooser table")[0];
    table.removeEventListener("mouseleave", this.handlerTableMouseLeave);

    let cellElements = domNode.querySelectorAll(".layoutChooser table td");
    cellElements.forEach((element: any) => {
      element.removeEventListener("touchstart", this.handlerCellMouseHover);
      element.removeEventListener("mouseenter", this.handlerCellMouseHover);
      element.removeEventListener("mouseleave", this.handlerCellMouseLeave);
      element.removeEventListener("click", this.handlerCellMouseClick);
    });
  };

  /**
   * @description 마우스커서가 표 영역에서 벗어난 경우에 발생된 이벤트를
   *              처리하는 이벤트 핸들러
   *              동작은, 셀에 표시된 모든 강조를 제거
   * @param event event 정보
   * @memberof LayoutChooser
   */
  handlerTableMouseLeave = (event: React.MouseEvent<any>) => {
    this.clearHighlight(); // 모든 셀 강조 제거
  };

  /**
   * @description 표의 셀 영역을 클릭한 경우에 발생한 이벤트를
   *              처리하는 이벤트 핸들러
   *              동작은, 클릭된 셀의 인덱스를 onCellClick callback에 파라미터로 넘겨주고 실행
   * @param event event 정보
   * @memberof LayoutChooser
   */
  handlerCellMouseClick = (event: React.MouseEvent<any>) => {
    const { onCellClick } = this.props; // 사용자로부터 전달받은 마우스 클릭 callback 함수

    ///
    /// 1. 클릭한 셀의 인덱스 획득
    ///
    const [rowIndex, columnIndex]: number[] = this.getCellIndexByElement(
      event.currentTarget
    );

    ///
    /// 2. onCellClick 콜백 함수 실행
    ///
    onCellClick && onCellClick(rowIndex, columnIndex);
  };

  /**
   * @description 마우스 커서가 표의 셀 영역에 들어온 경우에 발생되는 이벤트를
   *              처리하는 이벤트 핸들러
   *              동작은, 마우스가 올려진 셀은 강조 표시한다.
   * @param event event 정보
   * @memberof LayoutChooser
   */
  handlerCellMouseHover = (event: React.MouseEvent<any>) => {
    this.highlightCell(event.currentTarget);
  };

  /**
   * @description 마우스 커서가 표의 셀 영역에서 벗어난 경우에 발생되는 이벤트를
   *              처리하는 이벤트 핸들러
   *              동작은, 마우스가 셀의 영역을 벗어난 경우, 그 셀은 강조가 제거된다.
   * @param event event 정보
   * @memberof LayoutChooser
   */
  handlerCellMouseLeave = (event: React.MouseEvent<any>) => {
    this.highlightCell(event.currentTarget);
  };

  /**
   * @method getCellIndexByElement
   * @description 파라미터로 Cell의 HTMLElement 입력받고, 그 Cell의 행/열 인덱스를 리턴
   * @param element cell의 HTMLElement
   * @return
   *            rowIndex - 행 인덱스
   *            colIndex - 열 인덱스
   * @memberof LayoutChooser
   */
  getCellIndexByElement = (element: any) => {
    if (!_.get(element, "parentNode")) {
      return [-1, -1];
    }
    const rowElement: any = element.parentElement;
    const rowIndex: number = rowElement.rowIndex;
    const columnIndex: number = element.cellIndex;

    return [rowIndex, columnIndex] as [number, number];
  };

  /**
   * @method highlightCell
   * @description 입력받은 Cell에 강조 표시
   * @param element cell의 HTMLElement
   * @memberof LayoutChooser
   */
  highlightCell = (element: any) => {
    ///
    /// 1. Cell의 HTMLElement를 획득 하고, 모든 cell에서 hover 클래스를 제거
    ///
    const domNode: any = ReactDOM.findDOMNode(this);
    const cells = domNode.querySelectorAll(".layoutChooser table td");
    cells.forEach((cell: any, index: number) => {
      cell.classList.remove("hover");
    });

    ///
    /// 2. table HTMLElement를 찾고, 강조할 Cell의 행/렬 인덱스를 획득
    ///
    const table = domNode.querySelectorAll(".layoutChooser table")[0];
    const rowIndex = element.parentNode.rowIndex;
    const columnIndex = element.cellIndex;

    ///
    /// 3. 테이블의 각 행에 열을 순회하며, 강조표시를 적용
    ///
    for (let i = table.rows.length - 1; i >= 0; i--) {
      const row = table.rows[i];
      if (i <= rowIndex) {
        for (let j = row.cells.length - 1; j >= 0; j--) {
          if (j <= columnIndex) {
            const cell = row.cells[j];
            cell.classList.add("hover");
          }
        }
      }
    }
  };

  /**
   * @method clearHighlight
   * @description 표에서 모든 강조를 제거
   * @memberof LayoutChooser
   */
  clearHighlight = () => {
    const domNode: any = ReactDOM.findDOMNode(this);

    const cells = domNode.querySelectorAll(".layoutChooser table td");
    cells.forEach((cell: any, index: number) => {
      cell.classList.remove("hover");
    });
  };

  /**
   * @method renderTable
   * @description 행/열 속성 값 크기만큼, HTML태그를 를 이용해 표를 작성
   * @memberof LayoutChooser
   */
  renderTable() {
    let table = [];
    let rows = [];

    for (var i = 0; i < this.props.rows; i++) {
      let rowID = `row${i}`;
      let cell = [];
      for (var idx = 0; idx < this.props.columns; idx++) {
        let cellID = `cell${i}-${idx}`;
        cell.push(<td key={cellID} id={cellID} />);
      }
      rows.push(
        <tr key={rowID} id={rowID}>
          {cell}
        </tr>
      );
    }

    table.push(
      <table key="layout-chooser-table">
        <tbody>{rows}</tbody>
      </table>
    );

    return table;
  }

  /**
   * @method render
   * @description 컴포넌트의 UI를 렌더링
   * @memberof LayoutChooser
   * @return 렌더링된 React Element
   */
  render() {
    return (
      <div id="layoutChooser" className="layoutChooser dialog-animated">
        {this.renderTable()}
      </div>
    );
  }
}

export default LayoutChooser;
